import styled from "@emotion/styled"
import { css, keyframes } from "@emotion/react"
import Typography from "@mui/material/Typography"
import { Link } from "utils"
import { breakpoints } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"
import { colors } from "../../theme/colors"
import {
  HEADER_HOMEPAGE_HEIGHT_DESKTOP,
  HEADER_HOMEPAGE_HEIGHT_MOBILE,
} from "../../layout/Header/styles"

const fillProgress = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`

/* ------------------------------------------------------------------ */
/* EGDE-331 — Brand intro reveal + white-dip slide transition          */
/* ------------------------------------------------------------------ */

/* Intro phases are driven from JS via the $phase prop. */
export type IntroPhase = "reveal" | "expand" | "done"

/* EGDE-483/494: the prototype shows the icon-only mark first, then the
   wordmark + tagline slide out from behind it as ONE unit (Figma smart-
   animate) — QA rejected the previous per-character write-in. */
const iconIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`

/*
 * EGDE-494 point 5, r3. Three returns on this one point, and the measurements
 * say all three had the same cause: the entrance was a LAYOUT animation whose
 * travel finished long before its clock did.
 *
 * It animated `max-width: 0 -> 480px` on the text block. The lockup's text is
 * 140px wide at desktop and 103px at mobile, so the reveal was complete once
 * max-width crossed ~103px — and with the old easeOutCubic that happens 11% of
 * the way in. Probed at 375px: the text goes 0 -> 96px -> 103px between t=900ms
 * and t=1100ms. Nominally a 1.4s animation; geometrically a 150ms one. That is
 * QA's "it's moving too fast", and it is why lengthening the duration in r1
 * (0.8s -> 1.4s) changed nothing visible — it only stretched the dead tail.
 *
 * The travel is now expressed in TRANSFORMS, whose distances are percentages of
 * each element's own box, so the full duration is visible motion and no frame
 * costs a layout pass (Raz r3: "the entrance movement should be smoother and
 * more like sliding"). Composed, the three below reproduce the prototype's
 * geometry — spec point 5, "logo element moves to the right and the text moves
 * out from it to the left":
 *
 *   row   -50% of the row  +  icon +50% of the icon  =  icon starts centred on
 *   the lockup and glides to its seat, and the text — parked a full width away
 *   inside the clip — slides out from behind it.
 *
 * Timed off Raz's reference recording, where the reveal runs t=1.87s to t=2.70s
 * and progress tracks the clock almost 1:1 (12% of the travel at 16% of the
 * time, 33% at 52%, 75% at 88%). So: 1s, and a symmetric ease-in-out that keeps
 * the travel spread across the whole of it. The old cubic-bezier(0.33, 1, 0.68,
 * 1) put 60% of the movement in the first fifth, which is the other half of why
 * this read as a blink.
 *
 * Transforms are not mirrored by stylis-plugin-rtl, so --intro-edge supplies the
 * LTR mirror (same pattern as FormShell's --edge).
 */
const lockupRowIn = keyframes`
  from { transform: translateX(calc(var(--intro-edge) * -50%)); }
  to   { transform: translateX(0%); }
`

const iconSeatIn = keyframes`
  from { transform: translateX(calc(var(--intro-edge) * 50%)); }
  to   { transform: translateX(0%); }
`

const textSlideIn = keyframes`
  from { transform: translateX(calc(var(--intro-edge) * 100%)); }
  to   { transform: translateX(0%); }
`

/* The lockup entrance's shared clock. `both` matters: the travel sits behind a
   delay, and with `forwards` alone every element would park at its FINISHED
   position for the first 0.9s and then jump back to the start. */
const LOCKUP_SLIDE = "1s cubic-bezier(0.42, 0, 0.58, 1) 0.9s both"

/*
 * EGDE-494 point 5, r4. r3 made the travel real, and on a warm load it is: the
 * text crosses its full width across the whole second. QA still filed it as a
 * fade, and the reason is that the entrance and the splash it belongs to run on
 * TWO different clocks.
 *
 * These are CSS animations, so they start when the lockup MOUNTS. The splash's
 * own timeline does not — it waits for the aleph mask to decode first (EGDE-595,
 * capped at 1.2s). Warm cache, the mask is there in a few ms and the two clocks
 * agree. Cold or slow, they do not: probed with the mask held back 1.1s, the
 * text starts moving at t=700ms and is fully seated by t=1683ms, i.e. most of
 * the entrance is spent behind a cover that is still an unbroken white sheet.
 * By the time the window is punched out and the composition reads as the brand
 * screen, the wordmark is already in place — nothing was seen moving, which is
 * exactly "appears as fade in", and it is why this only ever reproduced for QA
 * on a real network and never locally.
 *
 * Holding the animations paused until the mask resolves puts the entrance back
 * on the splash's clock. `both` keeps every element pinned at its start frame
 * while paused, so the pause is invisible — the travel simply begins when the
 * splash does.
 */
const LOCKUP_PLAY_STATE = "animation-play-state: var(--intro-play, running);"

/*
 * Intro mechanic (EGDE-483, matching the Figma prototype): the aleph is a
 * WINDOW into the real first-slide image — a white cover with an aleph-shaped
 * hole punched out. On expand the window GROWS (the white layer scales up,
 * taking its punched hole with it) until the photo fills the viewport; the
 * magnified aleph arms are the big wedge shapes sweeping across mid-
 * transition in the prototype. QA rejected the previous in-place dissolve
 * (opacity crossfade) as not matching.
 * The overlay is FIXED and sits above the site header (z-index 1001/1002):
 * during the splash the whole viewport is the brand screen, and a GlobalStyle
 * rule holds the header back until the overlay unmounts.
 */
export const IntroRoot = styled.div<{ $phase: IntroPhase }>`
  position: fixed;
  inset: 0;
  /* Above the header (1001/1002) AND the MUI-layered notification banner —
     nothing may pierce the brand splash. */
  z-index: 2000;
  overflow: hidden;
  pointer-events: none;
  /* The expansion itself reveals the page; the root only snaps its opacity
     off in "done" to clean up any sub-pixel white slivers before unmount. */
  opacity: ${(p) => (p.$phase === "done" ? 0 : 1)};
  transition: opacity 0.3s ease-out;
`

/* White cover with the aleph shape punched out, so the real hero background
   behind shows only through the aleph window.

   EGDE-595 (r2): the hole used to be cut by compositing TWO mask layers (the
   glyph XOR a full-size gradient). Safari confined the result to the glyph
   layer's own box, so the cover painted as a small white square around the
   aleph and the hero photo showed everywhere else — QA's screenshot, twice.
   Current WebKit composites it correctly, so it cannot be reproduced here; the
   fix is therefore to stop depending on multi-layer compositing at all. A
   single mask layer needs no compositing: hero-aleph-cover.svg is an opaque
   sheet 8× the glyph box with the glyph already punched out of it.

   Because that sheet is far larger than the viewport, percentage
   `mask-position` (which resolves against container−image) can no longer place
   the glyph. The element is oversized instead — 200vw × 200vh — and the mask
   simply centred in it, so the element's own centre is what parks the glyph. The
   margins also give the sheet room to cover a tall narrow viewport.

   GLYPH_CENTRE reproduces where the two-layer version actually painted, which is
   the position design signed off on: `mask-position: center 39%` resolved to
   (viewportH − glyphH) × 0.39, putting the glyph's CENTRE at 39% of the viewport
   plus 11% of the glyph's own height — not at 39% as the value reads. */
const ALEPH_WIDTH_MOBILE = "min(64vw, 300px)"
const ALEPH_WIDTH_DESKTOP = "min(34vw, 460px)"
/* Glyph box : sheet box, baked into hero-aleph-cover.svg. */
const COVER_SCALE = 8
/* 0.11 × the glyph's height, and the glyph is 625/700 as tall as it is wide.
   Expressed against the parent (IntroRoot is `position: fixed; inset: 0`) rather
   than in vh: on mobile Safari 100vh is the URL-bar-less large viewport, which
   would drop the glyph below where the visible viewport's 39% actually is — and
   mobile Safari is the browser this ticket was reported on. */
const GLYPH_CENTRE = (alephWidth: string) =>
  `calc(39% + ${alephWidth} * ${0.11 * (625 / 700)})`

/* How far the window opens before the veil has dissolved. */
const EXPAND_SCALE = 14

/* EGDE-650: the window used to open with `transform: scale(14)`, which meant the
   browser rasterised the masked sheet ONCE at its resting size and then let the
   compositor blow that bitmap up 14x. The veil is still fully opaque at ~10x
   (transform runs 2.2s, opacity only starts fading at 1.4s), so QA saw the
   glyph's edges as hard pixel steps on mobile and desktop alike — the shape is a
   clean vector in the design, so the loss was ours.

   Growing the MASK instead of transforming the layer keeps the edge geometric:
   the mask is re-rasterised at each size, so it is only ever as sharp as its
   source — hence the switch to hero-aleph-cover.svg, whose hole is a pixel-for-
   pixel twin of the PNG's (5600x5000 sheet, 700x625 hole at 2450,2187) and has
   no resolution to run out of. mask-position stays centred, so the window still
   opens from the element's own centre exactly as scale(center) did. */
const coverMaskSize = (alephWidth: string, phase: IntroPhase) => {
  const sheet = phase === "reveal" ? COVER_SCALE : COVER_SCALE * EXPAND_SCALE
  return `
    -webkit-mask-size: calc(${alephWidth} * ${sheet}) auto;
    mask-size: calc(${alephWidth} * ${sheet}) auto;
  `
}

/* EGDE-650 (r2): "the logo now appears a bit pixelated before it zooms in. When
   it zooms in, it's sharp."

   Measured, at 1920x1080 and DPR 1, the window's edge has exactly ONE partly
   covered pixel — and on stretches of the glyph's tighter curves, none at all:
   pure white abutting the photo. That is antialiasing working normally; there is
   simply one sample per device pixel, and a 460px shape with this much curvature
   spends them on short straight runs. Which is why the expanded window reads as
   clean: at 14x the same steps are 14x longer and the eye sees a line.

   EGDE-650 (r3): "it's blurry before the animation" — the r2 cure was a 0.8px
   `filter: blur()` on a wrapper of the masked sheet, and it defocused more than
   the edge. The filter re-rasterises the whole composited layer, and the PHOTO
   showing through the punched window came out measurably softened (4% RMSE
   against the unfiltered frame, with the white sheet itself byte-identical) —
   which is exactly the complaint: soft while the filter is on (reveal), sharp
   the moment the expand phase switches it off. No CSS-side feather can work
   here. The softening now lives in the mask's own ALPHA instead: the glyph in
   hero-aleph-cover.svg carries a half-grey stroke that hands the edge a
   50%-coverage rim. Coverage can only shade edge pixels — the photo it
   reveals is composited at full fidelity.

   EGDE-740: that rim is specified in the glyph's own path units, so its width
   in real pixels follows the glyph — and ALEPH_WIDTH_MOBILE is roughly half
   ALEPH_WIDTH_DESKTOP. Mobile was getting half the ramp on twice the curvature
   per pixel and stair-stepped; the stroke widened to 0.095 path units so the
   narrow breakpoint gets the ramp the wide one already shipped. Anything that
   changes either width here changes that balance — the reasoning and the
   measurements live in hero-aleph-cover.svg.

   EGDE-740 (r2): "still pixelated/blurry", and r1 was measurably on the site
   when that was written — the two QA screenshots differ ONLY along the mask's
   edge contour, the photo inside the window being pixel-identical. r1 was just
   too small a step: it bought half a device pixel of ramp.

   The reported view is DevTools' responsive mode at 375x824 on a 1920x1080
   screen, i.e. DPR 1 — the glyph is 240 device px there against 720 on a real
   DPR-3 phone, and at DPR 3 the edge already reads clean. Since the rim is in
   path units it is fixed in CSS px, so no single width serves both: the ~3.8
   CSS px it takes to hide the treads at DPR 1 is ~11 device px of softness at
   DPR 3 and walks back into EGDE-650 r2's "blurry". Hence the swap below rather
   than another width — a resolution query inside the SVG cannot do it, because
   an SVG rasterised as an image has no device-pixel context (measured: DPR 1, 2
   and 3 all take the same branch inside the file).

   EGDE-740 (r3): the r2 graded ramp came back "Same". The r1 rejection
   screenshot shows what QA is actually pointing at: the arrows sit ON the edge
   contour, where the rim's partial coverage lets the dark photo bleed through —
   at the 240px glyph every rim reads as a grey halo around the cutout, and the
   graded ramp only made it bolder (measured sheet-to-photo ramps at DPR 1:
   graded ~6px, flat ~3px, bare path ~2px, and the bare path is pixel-identical
   to the same SVG drawn as a plain <img> — the browser's best edge at this
   size). The twin therefore now carries NO rim at the mobile resting size and
   keeps the accepted flat rim for desktop rest and both sweeps via its in-file
   width query, so only the reported case moves. */
export const IntroWhite = styled.div<{ $phase: IntroPhase }>`
  position: absolute;
  inset-inline: -50%;
  top: calc(${GLYPH_CENTRE(ALEPH_WIDTH_MOBILE)} - 100%);
  height: 200%;
  background: ${colors.primitives.neutral0};
  -webkit-mask-image: url("/hero-aleph-cover.svg");
  mask-image: url("/hero-aleph-cover.svg");
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
  /* EGDE-740 (r2): only where a device pixel is a CSS pixel — see the note
     above. The webkit device-pixel-ratio feature carries the same query on the
     older WebKit this splash was first reported on. */
  @media (max-resolution: 1.5dppx), (-webkit-max-device-pixel-ratio: 1.5) {
    -webkit-mask-image: url("/hero-aleph-cover-lowdpr.svg");
    mask-image: url("/hero-aleph-cover-lowdpr.svg");
  }
  /* EGDE-483: growing the sheet grows the punched hole with it — the aleph
     window swallows the viewport, its magnified arms sweeping across like the
     prototype's wedges. It is direction-agnostic (RTL-safe). The glyph's
     negative space (the gap BETWEEN its two strokes) also magnifies, so opening
     the window always leaves a giant white slash across the screen — the white
     therefore dissolves over the expansion's final stretch, exactly like the
     prototype's clearing veil.
     The mask stays centred, so the window opens from the element's own centre,
     which is parked on the glyph. */
  ${(p) => coverMaskSize(ALEPH_WIDTH_MOBILE, p.$phase)}
  opacity: ${(p) => (p.$phase === "reveal" ? 1 : 0)};
  /* EGDE-494 (r2): QA "it's moving too fast, not like Raz showed" — the sweep
     was 1.5s where Raz's recording takes about two seconds over it. Slowed to
     2.2s, with the veil's dissolve held proportionally later so the white still
     clears over the expansion's final stretch rather than ahead of it. */
  transition:
    -webkit-mask-size 2.2s cubic-bezier(0.65, 0, 0.35, 1),
    mask-size 2.2s cubic-bezier(0.65, 0, 0.35, 1),
    opacity 0.8s ease 1.4s;

  @media (min-width: ${breakpoints.lg}px) {
    top: calc(${GLYPH_CENTRE(ALEPH_WIDTH_DESKTOP)} - 100vh);
    ${(p) => coverMaskSize(ALEPH_WIDTH_DESKTOP, p.$phase)}
  }
`

/* Brand lockup (orange aleph icon + wordmark + tagline) beneath the aleph
   window, on the white. Centred with symmetric insets + auto margins so the
   position survives the RTL stylis flip untouched. */
export const IntroLockup = styled.div<{ $phase: IntroPhase; $ready: boolean }>`
  position: absolute;
  inset-inline: 0;
  margin-inline: auto;
  width: fit-content;
  /* EGDE-494 r4: the entrance rides the splash's clock, not the mount's — see
     LOCKUP_PLAY_STATE. Every animated part of the lockup reads this. */
  --intro-play: ${(p) => (p.$ready ? "running" : "paused")};
  /* EGDE-494: the design places the lockup clearly BELOW the aleph window
     (which bottoms out around 61–63vh) — it must not overlap the arms. */
  top: 71%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${sizes.spacing[8]}px;
  /* EGDE-494: inline direction of the lockup's travel, mirrored for the LTR
     locales because transforms are not flipped by stylis-plugin-rtl. */
  --intro-edge: 1;

  [dir="ltr"] & {
    --intro-edge: -1;
  }

  /* EGDE-483 (QA 2026-07-15): the lockup must disappear WITH the main
     animation, not after it — the old 0.9s delay kept the logo as the last
     readable element floating over the fully-revealed photo. The prototype
     fades the lockup as one unit with the clearing veil, so it now fades
     through the expansion's middle stretch and is gone before the white
     finishes dissolving. */
  opacity: ${(p) => (p.$phase === "reveal" ? 1 : 0)};
  transition: ${(p) =>
    p.$phase === "expand"
      ? "opacity 0.7s ease-out 0.3s"
      : "opacity 0.5s ease-out"};

  @media (min-width: ${breakpoints.lg}px) {
    top: 72%;
  }
`

/* The row is laid out at its FINAL width from the first frame — the entrance is
   carried entirely by transforms, so nothing here reflows mid-animation. The row
   starts shifted half its own width towards the text, which is what puts the
   icon on the lockup's centre. */
export const IntroLockupRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[12]}px;
  white-space: nowrap;
  animation: ${lockupRowIn} ${LOCKUP_SLIDE};
  ${LOCKUP_PLAY_STATE}
`

/* Fixed-width window on the text. The clip is what reveals the wordmark, so the
   text needs no opacity ramp of its own — glyphs cross a hard edge next to the
   icon exactly as they do in the prototype. */
export const IntroTextClip = styled.div`
  flex: 0 0 auto;
  overflow: hidden;
`

/* Wordmark + tagline column, parked one full width away inside the clip and
   sliding out from behind the icon. */
export const IntroTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[8]}px;
  animation: ${textSlideIn} ${LOCKUP_SLIDE};
  ${LOCKUP_PLAY_STATE}
`

/* Real brand lockup colors (EGDE-494): "אגד" near-black, "פלוס" orange. */
export const IntroWordmark = styled.div`
  font-weight: 700;
  font-size: 28px;
  line-height: 1;
  color: ${colors.light.text.primary};
  white-space: pre;

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 40px;
  }
`

export const IntroWordmarkAccent = styled.span`
  color: ${colors.primitives.primary500};
`

export const IntroTagline = styled.div`
  font-size: 14px;
  line-height: 1.3;
  color: ${colors.light.text.primary};
  white-space: pre;

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 18px;
  }
`

/* Gradient aleph mark inside a gradient ring — the real logo icon
   (EGDE-494), not the previous thin flat-orange circle. Fades in alone
   before the text emerges. */
export const IntroIcon = styled.div`
  position: relative;
  width: 44px;
  height: 44px;
  border-radius: ${sizes.borderRadius.full}px;
  background: ${colors.gradients.gradientCta};
  flex-shrink: 0;
  opacity: 0;
  /* EGDE-483: slowed with the text sweep so the icon's arrival is visible.
     EGDE-494: it also cancels half the row's shift, which lands it on the
     lockup's centre to begin with and glides it right to its seat. */
  animation:
    ${iconIn} 0.6s ease-out 0.25s forwards,
    ${iconSeatIn} ${LOCKUP_SLIDE};
  /* Applies to BOTH animations above — the icon's fade-in has to wait with the
     rest, or it would already be sitting there when the window opens. */
  ${LOCKUP_PLAY_STATE}

  @media (min-width: ${breakpoints.lg}px) {
    width: 60px;
    height: 60px;
  }

  /* white disc leaves the gradient visible only as the ring */
  &::after {
    content: "";
    position: absolute;
    inset: 7%;
    border-radius: ${sizes.borderRadius.full}px;
    background: ${colors.primitives.neutral0};
  }

  &::before {
    content: "";
    position: absolute;
    inset: 24%;
    z-index: 1;
    background: ${colors.gradients.gradientCta};
    -webkit-mask-image: url("/hero-aleph-fill.png");
    mask-image: url("/hero-aleph-fill.png");
    -webkit-mask-size: contain;
    mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-position: center;
  }
`

/* ------------------------------------------------------------------ */
/* Root — full-bleed hero section                                      */
/* ------------------------------------------------------------------ */
export const Root = styled.div`
  position: relative;
  width: 100%;
  height: 100dvh;
  overflow: hidden;
`

/* ------------------------------------------------------------------ */
/* Swiper wrapper — overrides global .swiper padding                   */
/* ------------------------------------------------------------------ */
export const SwiperWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;

  .swiper {
    padding: 0 !important;
    width: 100%;
    height: 100%;
  }

  .swiper-slide {
    width: 100% !important;
    height: 100% !important;
    background-position: center;
    background-size: cover;
  }
`

/* ------------------------------------------------------------------ */
/* Background slide image container                                    */
/* ------------------------------------------------------------------ */
export const SlideImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

/* ------------------------------------------------------------------ */
/* Responsive image wrappers — show mobile/desktop based on viewport   */
/* ESEIM-163 / ESEIM-340: Mobile was showing desktop image             */
/* ------------------------------------------------------------------ */
export const DesktopImage = styled.div`
  position: absolute;
  inset: 0;
  display: none;

  @media (min-width: ${breakpoints.lg}px) {
    display: block;
  }
`

export const MobileImage = styled.div`
  position: absolute;
  inset: 0;
  display: block;

  @media (min-width: ${breakpoints.lg}px) {
    display: none;
  }
`

/* ------------------------------------------------------------------ */
/* Video background for slides (ESEIM-341)                             */
/* EGDE-566 (r2): the breakpoint lives on the DesktopImage/MobileImage  */
/* wrapper this now sits inside. Hiding the video below lg here left    */
/* mobile with an empty hero whenever no Mobile Image was uploaded.     */
/* ------------------------------------------------------------------ */
export const SlideVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

/* ------------------------------------------------------------------ */
/* Dark overlay on top of background images                            */
/* ------------------------------------------------------------------ */
export const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  /* Keep the header zone clean so the header gradient controls that area.
     The hero darkening ramps in only below the header. */
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0px,
    rgba(0, 0, 0, 0) ${HEADER_HOMEPAGE_HEIGHT_MOBILE}px,
    rgba(0, 0, 0, 0.4) 260px
  );
  pointer-events: none;

  @media (min-width: ${breakpoints.lg}px) {
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 0px,
      rgba(0, 0, 0, 0) ${HEADER_HOMEPAGE_HEIGHT_DESKTOP}px,
      rgba(0, 0, 0, 0.4) 360px
    );
  }
`

/* ------------------------------------------------------------------ */
/* Content layer — on top of overlay                                   */
/* ------------------------------------------------------------------ */
export const ContentLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  /* EGDE-68: The bottom strip (carousel player + scroll indicator) is a normal
     flex child at the end of this column — see BottomStripOuter. ContentFadeIn
     (flex:1) fills the space ABOVE it, so the CTA/text can never overlap the
     player regardless of slide count. No magic bottom-reservation needed: the
     player always occupies its own band and the content is laid out around it. */
`

/* ------------------------------------------------------------------ */
/* Entrance animation wrapper (ESEIM-181)                              */
/* EGDE-331: no longer animates itself — the Figma "HP Transition"     */
/* prototype plays the hero in staged beats (background image first,   */
/* then the decorative X, then title + CTA, then the bottom strip), so */
/* each part carries its own delayed transition driven by $entered.    */
/* A single wrapper fade made all of them appear at once, which QA     */
/* read as "missing the animation transition".                         */
/* ------------------------------------------------------------------ */
export const ContentFadeIn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

/* ------------------------------------------------------------------ */
/* Main content area (image + text)                                    */
/* ESEIM-171: Replaced flex-wrap with column/row switch for LTR/RTL    */
/* ------------------------------------------------------------------ */
export const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  gap: ${sizes.spacing[32]}px;
  padding: ${HEADER_HOMEPAGE_HEIGHT_MOBILE + 16}px ${sizes.spacing[16]}px
    ${sizes.spacing[24]}px;
  overflow: hidden;
  /* ESEIM-171: Prevent horizontal overflow on narrow viewports */
  max-width: 100%;

  @media (min-width: ${breakpoints.lg}px) {
    flex-direction: row;
    align-items: center;
    /* EGDE-497: the X watermark and the text column pin to the two edges of
       the 1200px content grid (the same lines the header aligns to), instead
       of floating as a centered cluster. 1232 = 1200 content + the 16px side
       gutters, so below 1232px the content keeps its viewport breathing room. */
    justify-content: space-between;
    gap: ${sizes.spacing[64]}px;
    padding: ${HEADER_HOMEPAGE_HEIGHT_DESKTOP}px ${sizes.spacing[16]}px 0;
    max-width: 1232px;
    margin: 0 auto;
    width: 100%;
  }
`

/* ------------------------------------------------------------------ */
/* Decorative Egged X image                                            */
/* ------------------------------------------------------------------ */
export const DecorativeImageWrapper = styled.div<{ $entered?: boolean }>`
  position: relative;
  /* EGDE-497: mobile X matches the Figma hero — ~300px and horizontally
     centered (it was a 220px square hugging the inline-start edge). */
  width: 300px;
  height: 300px;
  border-radius: 15px;
  overflow: hidden;
  flex-shrink: 0;
  order: -1;
  align-self: center;

  /* EGDE-331 beat 2: after the background-only beat, the X-outline grows in
     (the Figma prototype assembles the X before any text appears). */
  opacity: ${(p) => (p.$entered ? 1 : 0)};
  transform: scale(${(p) => (p.$entered ? 1 : 0.5)});
  transition:
    opacity 0.8s ease-out 0.2s,
    transform 0.8s cubic-bezier(0.33, 1, 0.68, 1) 0.2s;

  @media (min-width: ${breakpoints.lg}px) {
    order: 0;
    align-self: auto;
    /* ESEIM-171: Constrain decorative image so it never pushes content
       outside the 1200px container on smaller desktop viewports */
    width: clamp(280px, 30vw, 540px);
    height: clamp(280px, 30vw, 540px);
    border-radius: 17px;
  }

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: none;
  }
`

/* ------------------------------------------------------------------ */
/* Text content container (title + subtitle + CTA)                     */
/* ESEIM-356: Mobile text/button aligned to flex-start (right in RTL)  */
/* ------------------------------------------------------------------ */
export const TextContent = styled.div<{ $entered?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[24]}px;
  width: 100%;

  /* EGDE-331 beat 3: title + rotating subtitle + CTA rise in once the
     decorative X has (mostly) assembled. */
  opacity: ${(p) => (p.$entered ? 1 : 0)};
  transform: translateY(${(p) => (p.$entered ? 0 : 24)}px);
  transition:
    opacity 0.6s ease-out 0.7s,
    transform 0.6s ease-out 0.7s;

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[40]}px;
    flex: 1;
    /* ESEIM-171: Prevent text from forcing minimum width that overflows
       the row layout when paired with the decorative image */
    min-width: 0;
    width: auto;
  }

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: none;
  }
`

/* ------------------------------------------------------------------ */
/* Title block — fixed line + rotating subtitle line                   */
/* ------------------------------------------------------------------ */
export const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[4]}px;
  width: 100%;
`

/* ------------------------------------------------------------------ */
/* Title text (H1 Bold, white)                                         */
/* ------------------------------------------------------------------ */
/* EGDE-442: per-slide titles crossfade in place.
   EGDE-497: only the ACTIVE title is in normal flow and sets the wrapper
   height — inactive titles overlay it absolutely. The previous grid stack
   sized the wrapper to the TALLEST sibling, so a wrapping inactive title
   left a dead band between the visible title and the subtitle row. The
   height swap happens while the incoming title is still transparent
   (EGDE-443 sequential fade), so no visible jump. */
export const TitleTextWrapper = styled.div`
  position: relative;
  width: 100%;
  /* EGDE-497 r2: the roll clips at the line box — see textRollIn/Out. */
  overflow: hidden;
`

/* EGDE-497 r2 (QA 2026-07-19: "the text should move from bottom to top —
   Not fixed"): the earlier fade-pause-24px-rise read as a crossfade on the
   deployed site. The prototype is a continuous ticker synced with the
   slide's horizontal travel: the outgoing line rolls up and OUT of the
   clipped line box while the incoming one rolls up INTO it from below —
   no fade, no dead gap. */
const textRollIn = keyframes`
  from {
    transform: translateY(110%);
  }
  to {
    transform: translateY(0);
  }
`

const textRollOut = keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(-110%);
  }
`

const textRollEase = "cubic-bezier(0.33, 1, 0.68, 1)"

export const TitleText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$active" && prop !== "$noRoll",
})<{ $active?: boolean; $noRoll?: boolean }>`
  color: ${colors.primitives.neutral0};
  text-align: start;
  width: 100%;
  ${(p) =>
    p.$active === false &&
    `
    position: absolute;
    top: 0;
    inset-inline-start: 0;
  `}
  /* The outgoing line stays fully visible for its whole upward exit, then
     snaps hidden; the incoming line is visible from the first frame of its
     roll. Initial render paints inactive lines hidden with no transition. */
  opacity: ${(p) => (p.$active === false ? 0 : 1)};
  transition: opacity 0s linear ${(p) => (p.$active === false ? "0.7s" : "0s")};
  ${(p) =>
    /* EGDE-726 r2: $noRoll marks the settled title the h1 swap just
       remounted — without it the fresh node would replay its roll-in at
       rest. The next slide change drops the flag, so the exit roll below
       still starts normally on this same node. */
    p.$noRoll
      ? css`
          animation: none;
        `
      : p.$active !== false
        ? css`
            animation: ${textRollIn} 0.7s ${textRollEase} both;
          `
        : css`
            animation: ${textRollOut} 0.7s ${textRollEase} both;
          `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`

/* ------------------------------------------------------------------ */
/* Subtitle row — rotating text + orange chevron                       */
/* ------------------------------------------------------------------ */
export const SubtitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[8]}px;
  width: 100%;
  justify-content: flex-start;
  min-height: 44px;

  @media (min-width: ${breakpoints.lg}px) {
    min-height: 64px;
  }
`

/* ------------------------------------------------------------------ */
/* Subtitle text wrapper — for crossfade animation                     */
/* ------------------------------------------------------------------ */
/* EGDE-565: like TitleTextWrapper (EGDE-497), only the ACTIVE subtitle is in
   normal flow and sizes the wrapper — a long subtitle that wraps to two lines
   grows the band instead of being clipped at the fixed 44/64px min-height
   (where its half-cleared roll overlapped the next line). */
export const SubtitleTextWrapper = styled.div`
  position: relative;
  flex: 1;
  overflow: hidden;
  min-height: 44px;

  @media (min-width: ${breakpoints.lg}px) {
    min-height: 64px;
  }
`

/* ------------------------------------------------------------------ */
/* Individual subtitle text — positioned for fade transition           */
/* ------------------------------------------------------------------ */
export const SubtitleText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$active",
})<{ $active: boolean }>`
  color: ${colors.primitives.neutral0};
  text-align: start;
  width: 100%;
  /* EGDE-565: inactive lines overlay absolutely; the active line stays in
     flow so the wrapper grows with wrapped (multi-line) subtitles and the
     ±110% roll — relative to the element's OWN height — always clears the
     clip box. (The previous absolute+height:100% stack clipped long
     subtitles at the 44/64px band and left the halves overlapping.) */
  ${(p) =>
    !p.$active &&
    `
    position: absolute;
    top: 0;
    inset-inline-start: 0;
  `}
  /* EGDE-497 r2: same continuous bottom-to-top roll as TitleText — the
     line with the chevron is the one QA singled out. */
  opacity: ${(p) => (p.$active ? 1 : 0)};
  transition: opacity 0s linear ${(p) => (p.$active ? "0s" : "0.7s")};
  ${(p) =>
    p.$active
      ? css`
          animation: ${textRollIn} 0.7s ${textRollEase} both;
        `
      : css`
          animation: ${textRollOut} 0.7s ${textRollEase} both;
        `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`

/* ------------------------------------------------------------------ */
/* Orange chevron accent                                               */
/* ------------------------------------------------------------------ */
export const ChevronAccent = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;

  /* EGDE-24: The chevron sits at the subtitle's inline-START (rendered FIRST in
     source — right of the text in RTL, left of the text in LTR per Figma) and
     must point INTO the text body.

     The GradientChevron glyph natively points LEFT (its vertex is on the left,
     the "<" shape).
       - RTL (he/ar): the text flows leftward from the right edge, so the chevron
         must point LEFT — the natural glyph, no transform.
       - LTR (en/ru): the text flows rightward from the left edge, so the chevron
         must point RIGHT — mirror the glyph with scaleX(-1).

     IMPORTANT: stylis-plugin-rtl does NOT flip transform values, and the prod
     CSS build STRIPS the noflip comment, so we must scope each direction with an
     explicit selector that survives the build rather than relying on noflip or
     on the RTL pipeline to invert the transform. The LTR (mirror) rule is the
     base; RTL resets to the natural glyph via the [dir="rtl"] selector. */
  svg {
    transform: scaleX(-1);
  }

  [dir="rtl"] & svg {
    transform: none;
  }
`

/* ------------------------------------------------------------------ */
/* CTA button styles shared between Link and anchor variants           */
/* ESEIM-173: Split into CtaLink (internal) and CtaAnchor (external)   */
/* EGDE-642: the CTA belongs to the slide now, so the buttons stack in  */
/* one slot and roll with the title and subtitle. "static" is the       */
/* single-button hero (no slide carries its own pair) — it keeps the    */
/* pre-EGDE-642 behaviour of not animating on rotation.                 */
/* ------------------------------------------------------------------ */
export type CtaState = "static" | "active" | "inactive"

const ctaStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 200px;
  padding: ${sizes.spacing[12]}px ${sizes.spacing[16]}px;
  background: ${colors.gradients.gradientCta};
  border-radius: ${sizes.borderRadius.full}px;
  color: ${colors.light.text.primary};
  font-weight: 700;
  font-size: 18px;
  line-height: 22px;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 20px;
    line-height: 28px;
  }
`

/* EGDE-642: same roll as TitleText/SubtitleText so the button changes in step
   with the headline it belongs to. `visibility` rather than opacity does the
   snap here — it leaves the hover fade in ctaStyles intact, and it also drops
   the outgoing button out of the tab order and the accessibility tree for the
   0.7s it is still on screen. */
const ctaRollStyles = (state: CtaState) =>
  state === "static"
    ? null
    : css`
        ${state === "inactive" &&
        `
        position: absolute;
        top: 0;
        inset-inline-start: 0;
        pointer-events: none;
      `}
        visibility: ${state === "active" ? "visible" : "hidden"};
        transition:
          visibility 0s linear ${state === "active" ? "0s" : "0.7s"},
          opacity 0.2s ease;
        animation: ${state === "active" ? textRollIn : textRollOut} 0.7s
          ${textRollEase} both;

        @media (prefers-reduced-motion: reduce) {
          animation: none;
          transition: none;
        }
      `

export const CtaLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== "$state",
})<{ $state: CtaState }>`
  ${ctaStyles}
  ${(p) => ctaRollStyles(p.$state)}
`

export const CtaAnchor = styled("a", {
  shouldForwardProp: (prop) => prop !== "$state",
})<{ $state: CtaState }>`
  ${ctaStyles}
  ${(p) => ctaRollStyles(p.$state)}
`

/* EGDE-642: reserved band for the rotating CTA. Mirrors TitleTextWrapper — the
   ACTIVE button is in normal flow and sets the height, the others overlay it
   absolutely, and the roll is clipped at that box. The min-height keeps the
   band from collapsing on slides that carry no button, and the full width keeps
   the outgoing button from being clipped horizontally while it rolls out of a
   slot whose active slide has none. */
export const CtaSlot = styled.div`
  position: relative;
  width: 100%;
  min-height: 46px;
  overflow: hidden;
  text-align: start;

  @media (min-width: ${breakpoints.lg}px) {
    min-height: 52px;
  }
`

/* ------------------------------------------------------------------ */
/* Bottom strip — scroll indicator + nav + pause button                */
/* ------------------------------------------------------------------ */
export const BottomStripOuter = styled.div<{ $entered?: boolean }>`
  /* EGDE-68: In normal flow (flex child of ContentLayer) and pinned to the
     bottom of the column via flex-shrink:0. It takes its own intrinsic height,
     so the carousel player sits in a dedicated band that the content above can
     never overlap — at any slide count. (Previously position:absolute with a
     magic padding-bottom on ContentLayer that had to guess the strip height.) */
  flex-shrink: 0;
  width: 100%;
  z-index: 3;
  border-top: 1px solid rgba(255, 255, 255, 0.24);

  /* EGDE-331 beat 4: the player/scroll strip fades in last. */
  opacity: ${(p) => (p.$entered ? 1 : 0)};
  transition: opacity 0.6s ease-out 1.1s;

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transition: none;
  }
`

export const BottomStrip = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${sizes.spacing[24]}px ${sizes.spacing[16]}px;

  @media (min-width: ${breakpoints.lg}px) {
    max-width: 1200px;
    margin: 0 auto;
    width: 100%;
    /* ESEIM-171: Match MainContent horizontal padding so controls
       never touch the viewport edge on narrow desktops */
    padding: ${sizes.spacing[24]}px ${sizes.spacing[16]}px;
  }
`

/* ------------------------------------------------------------------ */
/* Scroll indicator (icon + text)                                      */
/* ------------------------------------------------------------------ */
export const ScrollIndicator = styled.div`
  display: flex;
  align-items: center;
  /* EGDE-68: tighter gap between the scroll label and its button on mobile so
     the cluster sits closer to the right edge (QA: "align more to the right").
     Desktop keeps the original 16px. */
  gap: ${sizes.spacing[8]}px;

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[16]}px;
  }

  /* EGDE-496: on the Figma circle-button sheet the CHEVRON variant hovers to
     a black glyph on the white disc — the orange hover glyph belongs to the
     play/pause variants only. */
  & button:hover {
    color: ${colors.light.text.primary};
  }
`

/* ------------------------------------------------------------------ */
/* Circular icon button (scroll / pause)                               */
/* ------------------------------------------------------------------ */
export const CircleIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${sizes.borderRadius.full}px;
  background-color: rgba(0, 0, 0, 0.5);
  border: 1px solid ${colors.primitives.neutral500};
  backdrop-filter: blur(4px);
  color: ${colors.primitives.neutral0};
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;

  /* EGDE-504: the Figma circle-button sheet pairs the dark on-image disc with
     a white-disc hover carrying the orange glyph. */
  &:hover {
    background-color: ${colors.primitives.neutral0};
    border-color: ${colors.primitives.neutral200};
    color: ${colors.primitives.primary500};
  }
`

/* ------------------------------------------------------------------ */
/* Scroll indicator text                                               */
/* ------------------------------------------------------------------ */
export const ScrollText = styled(Typography)`
  color: ${colors.primitives.neutral0};
`

/* ------------------------------------------------------------------ */
/* Carousel navigation container (bars + pause)                        */
/* ------------------------------------------------------------------ */
export const CarouselNavContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[12]}px;
`

/* ------------------------------------------------------------------ */
/* Carousel navigation bars wrapper                                    */
/* ------------------------------------------------------------------ */
export const CarouselNavBars = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[8]}px;
`

/* ------------------------------------------------------------------ */
/* Individual carousel nav bar                                         */
/* ------------------------------------------------------------------ */
/* EGDE-287: each bar is a button so clicking an indicator jumps to its slide.
   Vertical padding gives a usable tap target while the visible bar stays 4px. */
export const NavBarButton = styled.button`
  appearance: none;
  border: none;
  background: none;
  padding: 10px 0;
  margin: 0;
  display: flex;
  align-items: center;
  cursor: pointer;
`

export const NavBar = styled.div`
  width: 28px;
  height: 4px;
  border-radius: ${sizes.borderRadius.full}px;
  background-color: rgba(158, 158, 158, 0.5);
  position: relative;
  overflow: hidden;
`

/* ------------------------------------------------------------------ */
/* Progress fill inside nav bar                                        */
/* ------------------------------------------------------------------ */
export const NavBarProgress = styled.div<{
  $state: "idle" | "active" | "completed"
  $duration: number
}>`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background-color: ${colors.primitives.neutral0};
  /* EGDE-504: no @noflip — stylis-rtl flips this to "right" in RTL so the
     fill grows in reading direction, matching the prototype (prod builds
     strip @noflip comments, so relying on one splits dev from prod). */
  transform-origin: left;
  transform: scaleX(${(p) => (p.$state === "idle" ? 0 : 1)});
  ${(p) =>
    p.$state === "active"
      ? css`
          animation: ${fillProgress} ${p.$duration}ms linear forwards;
        `
      : ""}
`
